import InfoIcon from "@mui/icons-material/Info";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Button,
  Checkbox,
  type CheckboxProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  type TextFieldProps,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps } from "@mui/material/styles";
import { useField } from "formik";

import React, { useState } from "react";

import DateInput from "components/Common/DateInput";
import at from "helpers/at";
import countrycodes from "helpers/phone-lib.json";
import splitStringWithCountryCode from "helpers/splitStringWithCountryCode";
import styles from "./form-field.module.scss";
import Money from "./Money";

type InputMode = "none" | "text" | "email" | "numeric" | "decimal";
export type InputType =
  | InputMode
  | "password"
  | "date"
  | "money"
  | "checkbox"
  | "file"
  | "phone"
  | "number";
export interface InputFieldProps {
  /**Name of field */
  name: string;
  /**Label for the input */
  label: string | React.ReactNode;
  /**Input type of field */
  type?: InputType;
  /**Custom class */
  className?: string;
  /**Readonly value */
  value?: string | number | boolean | Date;
  /**Suggestive text */
  placeholder?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;

  /**If `yes` the field becomes multi-line*/
  multiline?: boolean;
  rows?: TextFieldProps["rows"];
  /**Allow negative numbers */
  allowNegative?: boolean;

  fullWidth?: boolean;

  autoComplete?: "on" | "off";
  /**Applies only for text inputs */
  textfieldProps?: TextFieldProps;

  tabIndex?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
  setDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  decimalScale?: any;
  sx?: SxProps;
  onChange?: TextFieldProps["onChange"];
  onCheckboxChange?: CheckboxProps["onChange"];
  /**Date format `eg: DD-MM-YYYY` */
  dateFormat?: string;
  labelClassName?: string;
  labelClassSx?: SxProps;
  labelRequiredClassName?: string;
  labelRequiredClassSx?: SxProps;
  helpText?: string;
  variant?: TextFieldProps["variant"];
  required?: boolean;
}

const InputField = (props: InputFieldProps) => {
  const {
    type = "text",
    name,
    label,
    className = "",
    style,
    value,
    disabled,
    placeholder,
    multiline,
    rows,
    allowNegative,
    fullWidth,
    textfieldProps,
    tabIndex,
    variant = "outlined",
    onChange = undefined,
    onCheckboxChange,
    autoComplete = "on",
    dateFormat = "YYYY-MM-DD",
    labelClassName = "",
    labelRequiredClassName = "",
    sx,
    helpText,
    required,
    labelClassSx,
    labelRequiredClassSx,
  } = props;

  const [field, meta, helpers] = useField(name);
  const [touched, error] = at(meta, ["touched", "error"]);
  const isError: boolean = touched && error && true;
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const { onBlur } = field;

  const Label = () => (
    <Stack
      alignItems="center"
      justifyContent="start"
      spacing={0.5}
      direction="row"
      mb={0.5}
    >
      {label && typeof label === "string" && (
        <Typography fontWeight="bold">{label && `${label}`}</Typography>
      )}
      {label && typeof label === "object" && label}
      {required && <Typography color="error">*</Typography>}
      {Boolean(helpText) && (
        <Tooltip title={helpText} enterTouchDelay={0} leaveDelay={200}>
          <InfoIcon color="primary" sx={{ marginTop: "6px" }} />
        </Tooltip>
      )}
      <InputLabel />
    </Stack>
  );

  switch (type) {
    case "date":
      return (
        <DateInput
          labelClassName={labelClassName}
          wrapperClassName={styles.datePickerWrapper}
          name={name}
          label={label as string}
          value={value || field.value}
          setValue={helpers.setValue}
          error={error}
        />
      );
    case "money":
      return (
        <FormControl
          className={className}
          error={isError}
          style={style}
          data-testid={`${type}-field-${name}`}
        >
          {label && <Label />}
          <Money
            name={name}
            onChange={helpers.setValue}
            className={className}
            isError={isError}
            style={style}
            // label={label}
            tabIndex={tabIndex}
            type={type}
            onBlur={onBlur}
            textfieldProps={textfieldProps}
            value={value || field.value}
            variant={variant}
            sx={sx}
            allowNegative={allowNegative}
            disabled={disabled}
            fullWidth={fullWidth}
            error={error}
            decimalScale={props.decimalScale}
          />
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );

    case "checkbox":
      return (
        <Grid container spacing={1} alignItems="center" justifyContent="start">
          <Grid item>
            <FormControlLabel
              className={labelClassName}
              control={
                <Checkbox
                  name={name}
                  onBlur={onBlur}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                    checked: boolean
                  ) => [
                    onCheckboxChange
                      ? onCheckboxChange(event, checked)
                      : field.onChange(event),
                  ]}
                  value={Boolean(field.value)}
                  inputProps={{ tabIndex }}
                  className={className}
                  disabled={disabled}
                  checked={Boolean(field.value)}
                  sx={sx}
                />
              }
              label={label}
              sx={{ marginRight: 0 }}
            />
          </Grid>
          {Boolean(helpText) && (
            <Tooltip
              title={<Grid container>{helpText}</Grid>}
              enterTouchDelay={0}
            >
              <Grid item>
                <InfoIcon style={{ marginTop: "6px" }} />
              </Grid>
            </Tooltip>
          )}
        </Grid>
      );
    case "file":
      return (
        <FormControl
          className={className}
          error={isError}
          data-testid={`${type}-field-${name}`}
          style={style}
        >
          {label && <Label />}
          <Button
            variant="contained"
            className={styles.fileUpload}
            component="label"
          >
            <div className={`${styles.uploadBtnPlaceholder}`}>
              Select jpg,png or pdf
            </div>
            <div className={`${styles.uploadBtn}`}>Choose File</div>
            <input
              type="file"
              accept="application/pdf,image/x-png,image/jpeg,image/jpg"
              hidden
              onChange={(e) => {
                onChange && onChange(e);
                const selectedFile: File = (e?.target?.files?.[0] ??
                  []) as File;
                helpers.setValue(selectedFile);
              }}
            />
          </Button>
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    case "number":
      return (
        <FormControl className={className} error={isError} style={style}>
          {label && <Label />}
          <TextField
            inputProps={{ tabIndex, "data-testid": `${type}-field-${name}` }}
            onBlur={onBlur}
            onChange={field.onChange}
            {...textfieldProps}
            sx={sx}
            placeholder={placeholder}
            value={value || field.value}
            disabled={disabled}
            error={isError}
            name={name}
            type="number"
            variant={variant}
            margin="dense"
            fullWidth={fullWidth}
          />
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    case "phone":
      const countryCode = splitStringWithCountryCode(
        value || field.value || "+65",
        countrycodes
      ).countryCode;
      const country = countrycodes.find(
        (item) => item.code === countryCode
      )?.name;

      // Create a reference to the original field value to detect changes
      const originalValue = field.value;

      // Handle country selection without triggering immediate save
      const handleCountryChange = (e: any) => {
        e.stopPropagation();

        // Get the selected country code
        const newCountryCode = e.target.value;

        // Extract the phone number part without country code
        const { remainingString } = splitStringWithCountryCode(
          value || field.value || "+65",
          countrycodes
        );

        // Combine the new country code with the existing phone number
        const newValue = `${newCountryCode}${remainingString}`;

        // Update the field value
        helpers.setValue(newValue);
      };

      return (
        <FormControl
          className={`${className} ${styles.phoneInputWrapper}`}
          error={isError}
          data-testid={`${type}-field-${name}`}
          style={style}
          onClick={(e) => {
            // Stop event propagation but don't prevent default to allow dropdown to open
            e.stopPropagation();
          }}
        >
          {label && <Label />}
          <TextField
            autoComplete={autoComplete as any}
            inputProps={{ tabIndex }}
            onBlur={(e) => {
              // Call original onBlur
              field.onBlur(e);

              // Don't trigger save if just opening/closing dropdown
              if (field.value !== originalValue) {
                onBlur(e);
              }
            }}
            onChange={(e) => {
              // Call original onChange
              field.onChange(e);
            }}
            value={value || field.value || "+65"}
            disabled={disabled}
            InputProps={{
              ...props.textfieldProps?.["InputProps"],
              startAdornment: (
                <Tooltip title={country}>
                  <InputAdornment
                    position="start"
                    sx={{
                      maxWidth: "20%",
                      paddingLeft: 1,
                      zIndex: 1000,
                    }}
                    onClick={(e) => {
                      // Stop propagation but don't prevent default
                      e.stopPropagation();
                    }}
                  >
                    <Select
                      defaultValue="+65"
                      onChange={handleCountryChange}
                      value={countryCode}
                      className={styles.countrySelect}
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: "normal",
                        minWidth: "60px",
                        zIndex: 1001,
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflow: "auto",
                            zIndex: 9999,
                          },
                        },
                        container: document.body,
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                        slotProps: {
                          paper: {
                            onClick: (e) => {
                              e.stopPropagation();
                            },
                          },
                        },
                        PopoverClasses: {
                          root: styles.popoverRoot,
                        },
                      }}
                      variant="standard"
                      disableUnderline
                      onClick={(e) => {
                        // Just stop propagation but don't prevent default
                        e.stopPropagation();
                      }}
                    >
                      {Boolean(countrycodes.length) &&
                        countrycodes.map((item) => (
                          <MenuItem
                            key={item.name}
                            value={item.code}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </InputAdornment>
                </Tooltip>
              ),
            }}
            {...textfieldProps}
            sx={{
              ...sx,
              ".MuiInputAdornment-root": {
                ".MuiInputBase-root": {
                  "&:hover": {
                    borderBottom: "none !important",
                  },
                  "&::after": {
                    borderBottom: "none !important",
                  },
                  "&::before": {
                    borderBottom: "none !important",
                  },
                },
                "&:focus-visible": {
                  outline: "none",
                },
                zIndex: 1000,
              },
            }}
            name={name}
            rows={rows}
            error={isError}
            multiline={multiline}
            placeholder={placeholder}
            type="text"
            variant={variant}
            margin="dense"
            fullWidth={fullWidth}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />

          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    case "password":
      return (
        <FormControl
          className={className}
          error={isError}
          data-testid={`${type}-field-${name}`}
          style={style}
        >
          {label && <Label />}
          <TextField
            inputProps={{ tabIndex }}
            onBlur={onBlur}
            onChange={field.onChange}
            {...textfieldProps}
            multiline={multiline}
            error={isError}
            name={name}
            autoComplete={autoComplete}
            value={value || field.value}
            placeholder={placeholder}
            sx={sx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    edge="end"
                  >
                    {value || field.value ? (
                      <VisibilityOff tabIndex={-1} />
                    ) : (
                      <Visibility tabIndex={-1} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled={disabled}
            type={showPassword ? "text" : "password"}
            variant={variant}
            margin="dense"
            fullWidth={fullWidth}
          />
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    case "email":
      return (
        <FormControl
          className={className}
          error={isError}
          data-testid={`${type}-field-${name}`}
          style={style}
        >
          {label && <Label />}
          <TextField
            inputProps={{ tabIndex }}
            onBlur={onBlur}
            onChange={field.onChange}
            {...textfieldProps}
            sx={sx}
            rows={rows}
            name={name}
            error={isError}
            multiline={multiline}
            value={(value || field.value)?.trim()}
            placeholder={placeholder}
            disabled={disabled}
            type="email"
            variant={variant}
            margin="dense"
            fullWidth={fullWidth}
          />
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
    default:
      return (
        <FormControl
          className={className}
          error={isError}
          data-testid={`${type}-field-${name}`}
          style={style}
        >
          {label && <Label />}
          <TextField
            inputProps={{ tabIndex }}
            onBlur={onBlur}
            onChange={field.onChange}
            {...textfieldProps}
            sx={sx}
            name={name}
            rows={rows}
            error={isError}
            multiline={multiline}
            value={value || field.value}
            placeholder={placeholder}
            disabled={disabled}
            type="text"
            variant={variant}
            margin="dense"
            fullWidth={fullWidth}
          />
          {isError && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      );
  }
};
export default InputField;
